import { useLocalStorageValue } from '@react-hookz/web';
import { XIcon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { T, useGT } from 'gt-react';
import config from 'src/config';
import { useContext } from 'react';
import { AuthContext } from '@/context/auth.context';
import { isGentlyAIUser } from '@/utils/user';

/**
 * Per-variant appearance and persistence. Separate storage keys so dismissing
 * one bar never hides the other.
 *
 * `bg-red-700` is a deliberately darker red so white text clears AA contrast at
 * 10px (~6.4:1). The test bar inverts that — amber-500 is the app's de facto
 * warning gold, and dark amber-950 text on it reads ~8:1.
 */
const VARIANTS = {
  production: {
    storageKey: 'gently.productionBannerDismissed',
    colors: 'bg-red-700 text-white',
    hover: 'hover:bg-red-800',
    pulse: 'prod-env-pulse',
    shortLabel: 'PROD',
  },
  test: {
    storageKey: 'gently.testEnvBannerDismissed',
    colors: 'bg-amber-500 text-amber-950',
    hover: 'hover:bg-amber-600',
    pulse: 'test-env-pulse',
    shortLabel: 'TEST',
  },
} as const;

type BannerVariant = keyof typeof VARIANTS;

/**
 * Persistent deployment-environment indicator.
 *
 * Driven by `config.deployEnv` (from `REACT_APP_DEPLOY_ENV`), NOT by
 * `config.isDev` / `NODE_ENV`. NODE_ENV is a BUILD flag — it is `production`
 * on staging and demo builds too, so it cannot tell these environments apart.
 * Only `DEPLOY_ENV` identifies the deployment.
 *
 * Two variants, never both:
 *
 * | deploy env           | bar                      | audience     |
 * | -------------------- | ------------------------ | ------------ |
 * | `production`         | red, "Production…"       | Gently users |
 * | `staging`/`sandbox`  | gold, "Test environment" | every user   |
 *
 * The production bar is an internal safety rail, so it stays limited to Gently
 * staff. The test bar is the opposite: its whole job is telling whoever is
 * looking at the screen — customer, tester, salesperson — that nothing here is
 * real, so it shows to everyone.
 *
 * Demo environments deliberately show NEITHER: they are `demo`, which is not a
 * production deployment and not a test one (a TEST ENVIRONMENT bar across a
 * customer demo undermines the demo). See packages/shared-types/src/deployEnv.ts.
 */
export function DeploymentEnvBanner() {
  const gt = useGT();
  const { currentUser } = useContext(AuthContext);

  if (config.isProductionDeploy && isGentlyAIUser(currentUser)) {
    return (
      <EnvBar
        variant="production"
        message={gt(
          'Production environment — changes here affect real customer data.',
        )}
        expandTitle={gt(
          'Production environment — click to show the full banner',
        )}
        hideLabel={gt('Hide production banner')}
      >
        <T context="persistent banner shown on production deployments to warn that changes affect real data">
          Production environment — changes here affect real customer data.
        </T>
      </EnvBar>
    );
  }

  if (config.isTestDeploy) {
    return (
      <EnvBar
        variant="test"
        message={gt('Test environment — this is not production data.')}
        expandTitle={gt('Test environment — click to show the full banner')}
        hideLabel={gt('Hide test environment banner')}
      >
        <T context="persistent banner shown on staging/sandbox deployments to warn that this is not the real system">
          Test environment — this is not production data.
        </T>
      </EnvBar>
    );
  }

  return null;
}

/**
 * The strip itself.
 *
 * Both states are `fixed`, so this never occupies layout space or pushes the
 * page down. Dismissing collapses the full-width bar to a slowly pulsing
 * marker pinned at (0, 0), sized to the sidenav's current width; clicking that
 * marker brings the bar back. Dismissal persists in localStorage.
 */
function EnvBar({
  variant,
  message,
  expandTitle,
  hideLabel,
  children,
}: {
  variant: BannerVariant;
  /** Plain-text copy for the `title` tooltip — the translated `children` is what renders. */
  message: string;
  expandTitle: string;
  hideLabel: string;
  children: ReactNode;
}) {
  const { colors, hover, pulse, shortLabel, storageKey } = VARIANTS[variant];
  const { value: dismissed, set: setDismissed } = useLocalStorageValue<boolean>(
    storageKey,
    { defaultValue: false, initializeWithValue: true },
  );

  if (dismissed) {
    return (
      // Keeps the bar's colors, but sized to the sidenav's CURRENT width — it
      // tracks the sidebar's own collapse (16rem → 3rem) via the same CSS vars
      // and transition, which is why this renders inside SidebarProvider.
      // The slow background pulse keeps it noticeable at 12px tall.
      <button
        type="button"
        onClick={() => setDismissed(false)}
        title={expandTitle}
        style={{ animation: `${pulse} 3s ease-in-out infinite` }}
        className={`fixed left-0 top-0 z-50 flex h-3 w-(--sidebar-width) items-center justify-center text-[10px] font-bold leading-none tracking-wide transition-[width] duration-200 ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:w-(--sidebar-width-icon) ${colors}`}
      >
        {shortLabel}
      </button>
    );
  }

  return (
    // h-3 == 12px, the maximum height this strip is allowed to occupy;
    // leading-none centers the 10px text inside it. pointer-events-none keeps
    // the strip from swallowing clicks on the header edge underneath it.
    <div
      role="status"
      className={`pointer-events-none fixed inset-x-0 top-0 z-50 flex h-3 items-center justify-center text-[10px] font-medium leading-none tracking-wide ${colors}`}
    >
      {/* px-6 keeps the text clear of the dismiss button, and truncate +
          title mean a narrow viewport shows an ellipsis with the full warning
          still reachable on hover rather than silently clipping it. */}
      <span className="truncate px-6" title={message}>
        {children}
      </span>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label={hideLabel}
        className={`pointer-events-auto absolute right-0 flex h-3 w-6 items-center justify-center ${hover}`}
      >
        <XIcon className="size-2" weight="bold" />
      </button>
    </div>
  );
}
